import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './dialog.css';
import { IComponentLifecycleProps } from '../../utils/react';

export interface IDialogProps extends IComponentLifecycleProps {
	onClose: (event: any) => Promise<void> | void;
	contentHeight?: boolean;
	children?: React.ReactNode;
}

export default function Dialog(props: IDialogProps) {
	const rootElementRef = useRef(null);
	const shouldCloseRef = useRef(false);

	useEffect(() => {
		const element = rootElementRef.current;

		if (!!element && document.contains(element)) {
			props.onMount?.(element);
		}

		// When the component is unmounted
		return () => {
			props.onUnmount?.();
		};
	}, []);

	useEffect(() => {
		const element = rootElementRef.current;

		props.onUpdate?.(element);
	});

	useLayoutEffect(() => {
		props.onBeforeUpdate?.();
	}, []);

	useEffect(() => {
		// Set overflow hidden on the body element to avoid the background of the dialog scrolling
		document.body.style.overflow = 'hidden';

		return () => {
			// Reset overflow to its default value
			document.body.style.overflow = 'auto';
		};
	}, []);

	const handleBackdropMouseDown = () => {
		shouldCloseRef.current = true; // started on backdrop
	};

	const handleDialogMouseDownCapture = () => {
		// If mousedown started inside the dialog, don't close on backdrop click.
		shouldCloseRef.current = false;
	};

	const handleBackdropClick = (e: React.MouseEvent) => {
		const selectedText = window.getSelection()?.type === 'Range';
		const selectedInput =
			(document.activeElement instanceof HTMLInputElement ||
				document.activeElement instanceof HTMLTextAreaElement) &&
			document.activeElement.selectionStart !==
				document.activeElement.selectionEnd;

		if (selectedText || selectedInput) {
			// Don't close the dialog if the user is selecting text or typing in an input
			return;
		}

		// Only close for primary button and when the press started on backdrop
		if (e.button === 0 && shouldCloseRef.current) {
			props.onClose?.(e);
		}
	};

	const contentHeight = props.contentHeight;

	return createPortal(
		<div
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				width: '100vw',
				height: '100vh',
				backgroundColor: 'rgba(0, 0, 0, 0.54)',
				zIndex: 1250,
				display: 'flex',
				justifyContent: 'center',
			}}
			onMouseDown={handleBackdropMouseDown}
			onClick={handleBackdropClick}
		>
			<div
				id='dialog-card'
				onClick={(e) => {
					// We want to stop the propagation of the click event so that it doesn't trigger the click event on the parent element
					// The parent element is the screen overlay, when clicked, we want to close the dialog.
					e.stopPropagation();
				}}
				onMouseDownCapture={handleDialogMouseDownCapture}
				style={{
					position: 'absolute',
					width: 'calc(100% - 160px)',
					...(contentHeight
						? { maxHeight: 'calc(100% - 160px)' }
						: { height: 'calc(100% - 160px)' }),
					maxWidth: '1200px',
					margin: '80px',
					backgroundColor: 'white',
					zIndex: 1,
					borderRadius: '8px',
					overflow: 'auto',
				}}
			>
				{props.children}
			</div>
		</div>,
		document.body
	);
}
